import React from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaPhoneSlash, FaEnvelopeOpenText, FaHourglassHalf, FaCheck } from 'react-icons/fa';
import SEO, { SITE_URL } from './SEO';
import Footer from './Footer';
import LiveDemoStrip from './LiveDemoStrip';
import AuditForm from './AuditForm';
import './AuditLandingPage.css';

function useQueryParam(name) {
  const { search } = useLocation();
  return new URLSearchParams(search).get(name);
}

function AuditLandingPage() {
  const src = useQueryParam('src') || 'direct';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Free Missed-Lead Audit',
    description:
      'UBOTIKA reviews your business website and phone flow, identifies where leads are leaking, and sends one custom AI-agent mockup tailored to your business.',
    provider: {
      '@type': 'LocalBusiness',
      name: 'UBOTIKA',
      url: SITE_URL,
      telephone: '+1-310-912-3548',
      email: 'hello@ubotika.com',
    },
    areaServed: 'United States',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  };

  return (
    <>
      <SEO
        title="Free Missed-Lead Audit | UBOTIKA"
        description="Find out where your business is losing leads. We review your website and phone flow, then send you one custom AI-agent mockup — free, within 48 hours."
        path="/free-missed-lead-audit"
        image={`${SITE_URL}/og/free-missed-lead-audit.png`}
        schema={schema}
      />
      <main id="main-content" className="audit-page">
        <section className="audit-hero audit-hero-v2">
          <div className="audit-hero-grid">
            <motion.div
              className="audit-hero-copy"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="audit-eyebrow">Free · No call required</div>
              <h1>Find out which leads you&apos;re losing.</h1>
              <ul className="audit-hero-bullets">
                <li><FaCheck className="audit-check" /> We review your website + phone flow by hand</li>
                <li><FaCheck className="audit-check" /> You get one custom AI-agent mockup for your business</li>
                <li><FaCheck className="audit-check" /> In your inbox within 48 hours — no call, no pitch</li>
              </ul>
            </motion.div>

            <div className="audit-form-wrap" id="audit-form">
              <AuditForm src={src} />
            </div>
          </div>
        </section>

        <section className="audit-pain">
          <div className="audit-section-inner">
            <h2 className="audit-section-h2">Most local businesses are losing leads here</h2>
            <div className="audit-pain-grid">
              <div className="audit-pain-card">
                <FaPhoneSlash className="audit-pain-icon" />
                <h3>Calls go unanswered</h3>
                <p>After hours, during service, when you're on another call — every missed ring is a customer dialing your competitor next.</p>
              </div>
              <div className="audit-pain-card">
                <FaEnvelopeOpenText className="audit-pain-icon" />
                <h3>Web forms sit in an inbox</h3>
                <p>Leads cool off in minutes. By the time you reply, they've already booked with someone else.</p>
              </div>
              <div className="audit-pain-card">
                <FaHourglassHalf className="audit-pain-icon" />
                <h3>Follow-up happens too late</h3>
                <p>The first business to follow up wins the job. If you're not first, you're competing on price.</p>
              </div>
            </div>
          </div>
        </section>

        <LiveDemoStrip
          location="audit-page"
          heading="While you wait for your audit — hear what we'd build you."
        />

        <Footer />
      </main>
    </>
  );
}

export default AuditLandingPage;
